import { Button, Grid, Card, Box, IconButton, Tooltip, Divider, Alert, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink } from "react-router-dom";
import { push } from "connected-react-router";
import { ListContextProvider, useListContext } from "ra-core";
import { FC } from "react";
import { useDispatch } from "react-redux";
import { Content } from "@/components/content/Content";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import { OdResourcePageProps } from "@/types/resource";
import useLocalListController from "@/components/list/useLocalListController";
import { DocumentTemplateResponseDto } from "@/gen/api/securefile";
import { RecPatch } from "@/dataProvider/tools";
import TextField from "@/components/field/TextField";
import { FunctionField } from "@/components/field";
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "@/components/list/datagridFunctionWidgets";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";


const documentTypeListOptions : {sorters: Record<string, (a:DocumentTemplateResponseDto & RecPatch, b:DocumentTemplateResponseDto & RecPatch) => number>} = {
  sorters: {
    isEnabled: (a, b) => { 
      if(a.isEnabled === b.isEnabled){
        return (a.name || '').toLowerCase() < (b.name || '').toLowerCase() ? -1 : 1
      }else{
        return (a.isEnabled || 0) > (b.isEnabled || 0) ? -1 : 1 
      }
    },
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '').toLowerCase() < (b.name || '').toLowerCase() ? -1 : 1,
  }
}

const DocumentTypeList: FC<OdResourcePageProps> = (props) => {
  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  
  return (
    <ListContextProvider value={useLocalListController({ basePath: '' }, documentTypeListOptions)}>
      <DocumentTypeListBody {...props} />
    </ListContextProvider>
  )
}

const statusOptions = [
  { label: 'Enabled', value: 'Enabled' },
  { label: 'Disabled', value: 'Disabled' },
]

const map = {
  'Enabled': (d: DocumentTemplateResponseDto) => d.isEnabled === true,
  'Disabled': (d: DocumentTemplateResponseDto) => d.isEnabled === false,
}

const statusMatch = (d: DocumentTemplateResponseDto, fval:FilterOption) => {
  const fn = (map as any)[fval.value]
  return !!fn && fn(d)
}

const DocumentTypeListBody: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  const listCtx = useListContext()
  if (listCtx.currentSort.field !== 'isEnabled') {
    setTimeout(() => listCtx.setSort('isEnabled'))
  }
  const dispatch = useDispatch()

  return (
    <Content pageName="Document Types" resourceName="Document Types" breadcrumbs={options?.breadcrumbs}
      mainActions={
        <Button
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={() => dispatch(push(`/${resource}/create`))}
        >
          Create Document Type
        </Button>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 2 }}>
            A Document Type is used to categorize the type of sensitive document, such as W-2 forms. A file can only be classified as one Document Type.
          </Alert>
          <Card>
            <AtopFuncArea >
              <SearchField 
                sourceKey="dbName"
                match={(d: DocumentTemplateResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
                sx={{ width: 280 }}
                TextFieldProps={{ placeholder: 'Search by Name' }}
              />
              <FilterField 
                label="Status" 
                filterOptions={statusOptions} 
                sourceKey="isEnabled"
                match={statusMatch}
                sx={{ width: 140, ml: 'auto'}}
              />
            </AtopFuncArea>
            <Divider sx={{ pb: 0 }} />
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& span.MuiTypography-root.filename' : { maxWidth:400, wordBreak:'break-word' },
                  }}
                  {...listCtx}
                >
                  <TextField label="Name " source="name" sortable={false} />
                  <TextField label="Display Name" source="displayName" sortable={false} />
                  <TextField label="Description" source="description" sortable={false} />
                  <FunctionField 
                    source="isEnabled" 
                    label="Status" 
                    sortable={false}
                    render={(record?:RecPatch) => 
                      <Chip 
                        label={(record as DocumentTemplateResponseDto&RecPatch)?.isEnabled ? 'Enabled' : 'Disabled'}
                        color={((record as DocumentTemplateResponseDto&RecPatch)?.isEnabled ? 'success' : 'warning') as any}
                      />
                    }
                  />
                  {/* <TextField label="Base InfoType" source="baseInfoType" sortable={false} /> */}
                  <FunctionField label="Action" sortable={false} 
                    render={(d0?:RecPatch) => {
                      const d = d0 as DocumentTemplateResponseDto
                      return (
                        <Tooltip title="Edit">
                          <IconButton 
                            sx={{ 
                              color: 'text.primary' as any, 
                              '&:hover': { backgroundColor: 'action.hover' },
                            }}
                            component={RouterLink}
                            to={`/${resource}/${d.id as any}/show`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore 
            <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default DocumentTypeList;
